/**
 * Зеркало цветовых токенов из `tokens.css` для тех потребителей, которые
 * физически не умеют читать CSS-переменные: paint-свойства MapLibre и
 * canvas-паттерны. Значения должны совпадать с tokens.css буква в букву —
 * при правке цвета правятся оба файла.
 */
export const PALETTE = {
  canvas: '#EFF1EE',
  soft: '#F8F9F7',
  card: '#FFFFFF',
  ink: '#122019',
  muted: '#55635B',
  warm: '#EEE5D4',

  deep: '#0B5137',
  active: '#1A8E58',
  bright: '#56C47B',
  mint: '#BFE4C8',
  sage: '#E4EFE7',

  copperDeep: '#9A4429',
  copperActive: '#D26A3D',
  copperSoft: '#FBE9DE',

  line: '#E2E6E1',
  lineStrong: '#CDD5CE',
  track: '#E7EBE7',

  /* Семантическая шкала состояния — только слои данных (см. PALETTE-SPEC.md). */
  stOk: '#1A8E58',
  stOkDeep: '#0B5137',
  stProgress: '#E8B93B',
  stWarn: '#E0862E',
  stAlert: '#C4453C',
  stMissing: '#A7B0AB',
  stFuture: '#D8DED3',

  stProgressSoft: '#FBF1D4',
  stWarnSoft: '#FBEAD9',
  stAlertSoft: '#F8E3E1',
  stMissingSoft: '#EEF0EE',

  stProgressInk: '#7A5B0C',
  stWarnInk: '#8A4D15',
  stAlertInk: '#8F2F27',

  /* Частота (тепловая карта): одна краснота, светлое → тёмное; heat3 = stAlert. */
  heat1: '#E3948B',
  heat2: '#D5705F',
  heat3: '#C4453C',
  heat4: '#A0352D',
  heat5: '#6E221C',

  /* Вода. */
  aqua: '#2678B8',
  aquaSoft: '#D6E8F5',
  aquaDeep: '#1A5788',

  /* Культуры. */
  cropCotton: '#1A8E58',
  cropWheat: '#D9A441',
  cropPotato: '#9A4F14',
  cropFeed: '#7CB342',
  cropOther: '#7E6FA6',
  cropFallow: '#ECE1C4',
  cropUnknown: '#C9CFCA',
} as const;

/**
 * Имена токенов, которыми фикстуры (crops.json) называют цвет.
 * Схема запрещает hex в данных, поэтому имя резолвится здесь.
 */
const TOKEN_TO_HEX: Record<string, string> = {
  deep: PALETTE.deep,
  active: PALETTE.active,
  bright: PALETTE.bright,
  mint: PALETTE.mint,
  sage: PALETTE.sage,
  warm: PALETTE.warm,
  muted: PALETTE.muted,
  line: PALETTE.line,
  'copper-deep': PALETTE.copperDeep,
  'copper-active': PALETTE.copperActive,
  'copper-soft': PALETTE.copperSoft,

  // Культуры — собственное семейство (PALETTE-SPEC.md): «прочее» и «пар»
  // занимают большие площади, и зелёная рампа на них читалась как разные
  // ступени одного смысла. Медь под ними запрещена (кандидатность).
  'crop-cotton': PALETTE.cropCotton,
  'crop-wheat': PALETTE.cropWheat,
  'crop-potato': PALETTE.cropPotato,
  'crop-feed': PALETTE.cropFeed,
  'crop-other': PALETTE.cropOther,
  'crop-fallow': PALETTE.cropFallow,
  'crop-unknown': PALETTE.cropUnknown,

  // Состояние и вода — на случай, если фикстура назовёт цвет статусом.
  'st-ok': PALETTE.stOk,
  'st-ok-deep': PALETTE.stOkDeep,
  'st-progress': PALETTE.stProgress,
  'st-warn': PALETTE.stWarn,
  'st-alert': PALETTE.stAlert,
  'st-missing': PALETTE.stMissing,
  'st-future': PALETTE.stFuture,
  aqua: PALETTE.aqua,
  'aqua-soft': PALETTE.aquaSoft,
};

export function tokenColor(name: string | null | undefined, fallback: string = PALETTE.track): string {
  if (!name) return fallback;
  return TOKEN_TO_HEX[name] ?? fallback;
}

/** Последовательная зелёная рампа: от светлого к тёмному. */
export const GREEN_RAMP = [
  PALETTE.sage,
  PALETTE.mint,
  PALETTE.bright,
  PALETTE.active,
  PALETTE.deep,
] as const;

/** Последовательная шкала частоты (тепловая карта): одна краснота. */
export const HEAT_RAMP = [
  PALETTE.heat1,
  PALETTE.heat2,
  PALETTE.heat3,
  PALETTE.heat4,
  PALETTE.heat5,
] as const;

/** Шаг рампы по нормированной величине 0..1. Светлым шагам нужна обводка. */
export function rampStep(t: number): string {
  const clamped = Number.isFinite(t) ? Math.min(1, Math.max(0, t)) : 0;
  const index = Math.min(GREEN_RAMP.length - 1, Math.floor(clamped * GREEN_RAMP.length));
  return GREEN_RAMP[index]!;
}
